/*
 * <article-toc> — builds a table of contents from the headings inside the
 * article body, keeps the current section highlighted while scrolling, and
 * collapses into a disclosure on small screens.
 *
 * Markup contract (see sections/main-article.liquid):
 *   <article-toc data-target=".article__content" data-depth="h2-h3">
 *     <details><summary>…</summary>
 *       <nav><ol class="article-toc__list"></ol></nav>
 *     </details>
 *   </article-toc>
 *
 * Renders nothing (and hides itself) when the article has no headings.
 */
if (!customElements.get('article-toc')) {
  class ArticleToc extends HTMLElement {
    constructor() {
      super();
      this.headings = [];
      this.links = [];
      this.ticking = false;
      this.lastActiveIndex = -2;
      this.onScroll = this.onScroll.bind(this);
      this.onBreakpointChange = this.onBreakpointChange.bind(this);
    }

    connectedCallback() {
      this.list = this.querySelector('.article-toc__list');
      this.details = this.querySelector('.article-toc__details');

      const target = document.querySelector(this.dataset.target || '.article__content');
      if (!this.list || !target) {
        this.hide();
        return;
      }

      const selector = this.dataset.depth === 'h2' ? 'h2' : 'h2, h3';
      this.headings = Array.from(target.querySelectorAll(selector)).filter(
        (heading) => heading.textContent.trim() !== ''
      );

      if (this.headings.length === 0) {
        this.hide();
        return;
      }

      this.build();

      // Desktop shows the list expanded; mobile starts collapsed.
      this.mediaQuery = window.matchMedia('(min-width: 1024px)');
      this.onBreakpointChange();
      this.mediaQuery.addEventListener('change', this.onBreakpointChange);

      window.addEventListener('scroll', this.onScroll, { passive: true });
      this.setActive();
    }

    disconnectedCallback() {
      window.removeEventListener('scroll', this.onScroll);
      if (this.mediaQuery) this.mediaQuery.removeEventListener('change', this.onBreakpointChange);
    }

    onBreakpointChange() {
      if (!this.details) return;
      this.details.open = this.mediaQuery.matches;
    }

    /*
     * An article with no headings leaves the left column empty, so collapse
     * the whole rail rather than reserving a blank gutter.
     */
    hide() {
      this.hidden = true;
      const rail = this.closest('.article__rail');
      const layout = this.closest('.article__layout');
      if (!rail || !layout) return;
      const stillVisible = Array.from(rail.querySelectorAll(':scope > * > *')).some(
        (el) => el !== this && !el.hasAttribute('hidden')
      );
      if (!stillVisible) layout.classList.add('article__layout--rail-empty');
    }

    /* Stable, unique ids so the anchors survive repeated renders. */
    slugify(text, used) {
      const base =
        text
          .toLowerCase()
          .trim()
          .replace(/[^\p{L}\p{N}]+/gu, '-')
          .replace(/^-+|-+$/g, '') || 'section';

      let slug = base;
      let suffix = 2;
      while (used.has(slug) || document.getElementById(slug)) {
        slug = `${base}-${suffix++}`;
      }
      used.add(slug);
      return slug;
    }

    build() {
      const used = new Set();
      const fragment = document.createDocumentFragment();
      let currentSublist = null;

      this.headings.forEach((heading) => {
        if (!heading.id) heading.id = this.slugify(heading.textContent, used);

        const item = document.createElement('li');
        const link = document.createElement('a');
        link.className = 'article-toc__link';
        link.href = `#${heading.id}`;
        link.textContent = heading.textContent.trim();
        link.addEventListener('click', (event) => this.onLinkClick(event, heading));
        item.appendChild(link);

        const isChild = heading.tagName === 'H3' && currentSublist !== null;

        if (isChild) {
          item.className = 'article-toc__item article-toc__item--child';
          currentSublist.appendChild(item);
        } else {
          item.className = 'article-toc__item';
          fragment.appendChild(item);
          if (heading.tagName === 'H2') {
            currentSublist = document.createElement('ol');
            currentSublist.className = 'article-toc__sublist';
            item.appendChild(currentSublist);
          } else {
            currentSublist = null;
          }
        }

        this.links.push({ link, heading });
      });

      this.list.appendChild(fragment);
    }

    /* Sticky headers overlap anchor targets, so scroll manually. */
    get scrollOffset() {
      const headerHeight = parseInt(
        getComputedStyle(document.documentElement).getPropertyValue('--header-height'),
        10
      );
      return (Number.isNaN(headerHeight) ? 0 : headerHeight) + 24;
    }

    get scrollBehavior() {
      return window.matchMedia('(prefers-reduced-motion: reduce)').matches ? 'auto' : 'smooth';
    }

    onLinkClick(event, heading) {
      event.preventDefault();
      const top = heading.getBoundingClientRect().top + window.scrollY - this.scrollOffset;
      window.scrollTo({ top, behavior: this.scrollBehavior });
      history.replaceState(null, '', `#${heading.id}`);
      if (this.mediaQuery && !this.mediaQuery.matches && this.details) this.details.open = false;
    }

    onScroll() {
      if (this.ticking) return;
      this.ticking = true;
      window.requestAnimationFrame(() => {
        this.setActive();
        this.ticking = false;
      });
    }

    setActive() {
      const threshold = this.scrollOffset + 8;
      let activeIndex = -1;

      this.links.forEach(({ heading }, index) => {
        if (heading.getBoundingClientRect().top <= threshold) activeIndex = index;
      });

      // Past the end of the page the last entry stays lit.
      if (activeIndex === -1 && window.scrollY > 0) activeIndex = 0;

      this.links.forEach(({ link }, index) => {
        if (index === activeIndex) {
          link.setAttribute('aria-current', 'true');
        } else {
          link.removeAttribute('aria-current');
        }
      });

      if (activeIndex !== this.lastActiveIndex) {
        this.lastActiveIndex = activeIndex;
        this.revealActive(activeIndex);
      }
    }

    /*
     * The list scrolls internally once it is long, so keep the highlighted
     * entry visible instead of letting it drift out of the box.
     */
    revealActive(index) {
      const nav = this.querySelector('.article-toc__nav');
      const entry = this.links[index];
      if (!nav || !entry || index < 0) return;
      if (nav.scrollHeight <= nav.clientHeight) return;

      const navBox = nav.getBoundingClientRect();
      const linkBox = entry.link.getBoundingClientRect();
      if (linkBox.top >= navBox.top && linkBox.bottom <= navBox.bottom) return;

      nav.scrollTo({
        top: entry.link.offsetTop - nav.clientHeight / 2 + entry.link.offsetHeight / 2,
        behavior: this.scrollBehavior,
      });
    }
  }

  customElements.define('article-toc', ArticleToc);
}
